/**
 * Zentrale Digitalprodukt-Struktur von Rothus Medien.
 * Quelle: bestehende Mein-Rothus-Checkout-Function (action: public_catalog).
 * Preise werden NICHT lokal gepflegt – Änderungen im Rothus CRM wirken direkt.
 */
export const ROTHUS_CATALOG_URL =
  'https://thmyvcccuudbjjgnzpxz.supabase.co/functions/v1/mein-rothus-checkout';

/** Bestehende Rothus-Kaufstrecke (Login/Registrierung, Bestellübersicht, PostFinance/TWINT). */
export const ROTHUS_DIGITAL_BASE = 'https://rothusmedien.ch/digitalabo';
export const MEIN_ROTHUS_URL = 'https://rothusmedien.ch/mein-rothus/login';

export const GLIDE_DIGITAL_CODE = 'glide_digital_yearly';
export const COMBO_DIGITAL_CODE = 'combo_digital_yearly';

/** Übergabe eines Digitalprodukts in den zentralen Rothus-Kaufprozess. */
export const rothusCheckoutUrl = (productCode: string): string =>
  `${ROTHUS_DIGITAL_BASE}?product=${encodeURIComponent(productCode)}`;

export interface RothusDigitalProduct {
  id: string;
  product_code: string;
  product_type: string;
  brand: string;
  title: string;
  description?: string | null;
  price_chf: number;
  duration_months: number;
}

/** Fallback nur, falls die zentrale API technisch nicht erreichbar ist. */
const fallbackProducts: RothusDigitalProduct[] = [
  {
    id: 'fallback-glide',
    product_code: GLIDE_DIGITAL_CODE,
    product_type: 'subscription',
    brand: 'glide',
    title: 'glide Digital',
    description: 'Alle digitalen Ausgaben von glide während 12 Monaten.',
    price_chf: 29,
    duration_months: 12,
  },
  {
    id: 'fallback-combo',
    product_code: COMBO_DIGITAL_CODE,
    product_type: 'subscription',
    brand: 'combo',
    title: 'Wandermagazin + glide Digital',
    description: 'Kombi-Zugang zu allen digitalen Ausgaben beider Magazine während 12 Monaten.',
    price_chf: 49,
    duration_months: 12,
  },
];

const CACHE_TTL_MS = 5 * 60 * 1000;
let inFlight: Promise<RothusDigitalProduct[]> | null = null;
let cached: { data: RothusDigitalProduct[]; at: number } | null = null;

export const fetchRothusDigitalProducts = (): Promise<RothusDigitalProduct[]> => {
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return Promise.resolve(cached.data);
  if (inFlight) return inFlight;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  inFlight = fetch(ROTHUS_CATALOG_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ action: 'public_catalog' }),
    cache: 'no-store',
    signal: controller.signal,
  })
    .then(async (res) => {
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`Rothus catalog ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json?.subscription_products) ? json.subscription_products : null;
      if (!list) throw new Error('Rothus catalog invalid payload');
      const data = list as RothusDigitalProduct[];
      cached = { data, at: Date.now() };
      inFlight = null;
      return data;
    })
    .catch((err) => {
      clearTimeout(timeoutId);
      console.warn('Rothus digital catalog unavailable, using fallback:', err);
      inFlight = null;
      return fallbackProducts;
    });

  return inFlight;
};

export const findDigitalProduct = (
  products: RothusDigitalProduct[],
  code: string,
): RothusDigitalProduct | undefined =>
  products.find((p) => p.product_code === code) ??
  fallbackProducts.find((p) => p.product_code === code);
